<template>
  <div>
    <Head title="Reportes Personalizados" />

    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Reportes Personalizados</h1>
        <p class="mt-2 text-gray-600">
          Genera reportes personalizados seleccionando la cuenta, modelo, campos y formato de salida.
        </p>
      </div>

      <!-- Formulario de configuración -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <form @submit.prevent="generateReport">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Selección de cuenta -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Cuenta *
              </label>
              <select 
                v-model="form.account_id" 
                class="form-select w-full"
                required
                @change="onAccountChange"
              >
                <option value="">Selecciona una cuenta</option>
                <option 
                  v-for="account in accounts" 
                  :key="account.id" 
                  :value="account.id"
                >
                  {{ account.name }}
                </option>
              </select>
            </div>

            <!-- Selección de modelo -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Modelo *
              </label>
              <select 
                v-model="form.model" 
                class="form-select w-full"
                required
                @change="onModelChange"
              >
                <option value="">Selecciona un modelo</option>
                <option 
                  v-for="(modelName, modelClass) in models" 
                  :key="modelClass" 
                  :value="modelClass"
                >
                  {{ modelName }}
                </option>
              </select>
            </div>

            <!-- Formato de salida -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Formato de salida *
              </label>
              <div class="flex space-x-4">
                <label class="flex items-center">
                  <input 
                    v-model="form.format" 
                    type="radio" 
                    value="excel"
                    class="mr-2"
                    required
                  />
                  <span class="text-sm text-gray-700">Excel (.xlsx)</span>
                </label>
                <label class="flex items-center">
                  <input 
                    v-model="form.format" 
                    type="radio" 
                    value="pdf"
                    class="mr-2"
                    required
                  />
                  <span class="text-sm text-gray-700">PDF</span>
                </label>
              </div>
            </div>

            <!-- Botón de generación -->
            <div class="flex items-end">
              <button 
                type="submit" 
                class="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!canGenerate || loading"
              >
                <span v-if="loading" class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generando...
                </span>
                <span v-else>Generar Reporte</span>
              </button>
            </div>
          </div>

          <!-- Selección de campos -->
          <div class="mt-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">
              Campos a incluir *
            </label>
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <label 
                v-for="(fieldName, fieldKey) in availableFields" 
                :key="fieldKey"
                class="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input 
                  v-model="form.fields" 
                  type="checkbox"
                  :value="fieldKey"
                  class="mr-2"
                  required
                />
                <span class="text-sm text-gray-700">{{ fieldName }}</span>
              </label>
            </div>
            <div class="mt-3 flex justify-between items-center">
              <button 
                type="button" 
                class="text-sm text-blue-600 hover:text-blue-800"
                @click="selectAllFields"
              >
                Seleccionar todos
              </button>
              <button 
                type="button" 
                class="text-sm text-gray-600 hover:text-gray-800"
                @click="clearAllFields"
              >
                Limpiar selección
              </button>
            </div>
          </div>

          <!-- Filtros opcionales -->
          <div v-if="Object.keys(availableFields).length > 0" class="mt-6">
            <div class="border-t border-gray-200 pt-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Filtros opcionales</h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div v-for="(fieldName, fieldKey) in availableFields" :key="`filter-${fieldKey}`">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ fieldName }}
                  </label>
                  <input 
                    v-model="form.filters[fieldKey]" 
                    type="text"
                    class="form-input w-full"
                    :placeholder="`Filtrar por ${fieldName.toLowerCase()}`"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Vista previa de datos -->
      <div v-if="previewData.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">
            Vista previa ({{ previewData.length }} registros)
          </h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th 
                  v-for="field in form.fields" 
                  :key="field"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ availableFields[field] || field }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(row, index) in previewData.slice(0, 10)" :key="index">
                <td 
                  v-for="field in form.fields" 
                  :key="field"
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                >
                  {{ formatFieldValue(row[field]) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="previewData.length > 10" class="px-6 py-4 text-center text-sm text-gray-500">
            Mostrando los primeros 10 registros de {{ previewData.length }} totales
          </div>
        </div>
      </div>

      <!-- Mensajes de estado -->
      <div v-if="message" class="mt-4 p-4 rounded-lg" :class="messageClass">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'CustomReportsIndex',
  components: {
    Head,
  },
  layout: Layout,
  props: {
    accounts: {
      type: Array,
      required: true,
    },
    models: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        account_id: '',
        model: '',
        fields: [],
        format: 'excel',
        filters: {},
      },
      availableFields: {},
      previewData: [],
      loading: false,
      message: '',
      messageType: 'success',
    }
  },
  computed: {
    canGenerate() {
      return this.form.account_id && 
             this.form.model && 
             this.form.fields.length > 0 && 
             this.form.format
    },
    messageClass() {
      return {
        'bg-green-100 text-green-800 border border-green-200': this.messageType === 'success',
        'bg-red-100 text-red-800 border border-red-200': this.messageType === 'error',
        'bg-blue-100 text-blue-800 border border-blue-200': this.messageType === 'info',
      }
    },
  },
  methods: {
    onAccountChange() {
      this.form.model = ''
      this.form.fields = []
      this.availableFields = {}
      this.previewData = []
      this.form.filters = {}
      this.clearMessage()
    },
    
    async onModelChange() {
      if (!this.form.model) {
        this.availableFields = {}
        this.form.fields = []
        this.previewData = []
        this.clearMessage()
        return
      }

      try {
        // Crear FormData para enviar los datos
        const formData = new FormData()
        formData.append('model', this.form.model)

        const response = await fetch('/custom-reports/fields', {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json',
          },
          body: formData,
        })

        if (response.ok) {
          const data = await response.json()
          console.log('Fields received:', data) // Debug log
          this.availableFields = data
          this.form.fields = []
          this.form.filters = {}
          this.previewData = []
          this.clearMessage()
        } else {
          const errorData = await response.json()
          console.error('Server error:', errorData) // Debug log
          this.showMessage('Error al obtener campos del modelo: ' + (errorData.error || 'Error desconocido'), 'error')
        }
      } catch (error) {
        console.error('Error al obtener campos del modelo:', error)
        this.showMessage('Error al obtener campos del modelo: ' + error.message, 'error')
      }
    },

    selectAllFields() {
      this.form.fields = Object.keys(this.availableFields)
    },

    clearAllFields() {
      this.form.fields = []
    },

    formatFieldValue(value) {
      if (value === null || value === undefined) return '-'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => this.clearMessage(), 5000)
    },

    clearMessage() {
      this.message = ''
      this.messageType = 'success'
    },

    async generateReport() {
      if (!this.canGenerate) {
        this.showMessage('Por favor completa todos los campos requeridos', 'error')
        return
      }

      this.loading = true
      this.clearMessage()

      try {
        // Preparar los datos del formulario
        const formData = new FormData()
        formData.append('account_id', this.form.account_id)
        formData.append('model', this.form.model)
        formData.append('format', this.form.format)
        
        // Agregar campos
        this.form.fields.forEach(field => {
          formData.append('fields[]', field)
        })
        
        // Agregar filtros
        Object.keys(this.form.filters).forEach(filterKey => {
          if (this.form.filters[filterKey]) {
            formData.append(`filters[${filterKey}]`, this.form.filters[filterKey])
          }
        })

        // Enviar solicitud
        const response = await fetch('/custom-reports/generate', {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          },
          body: formData,
        })

        if (response.ok) {
          // Si es una descarga, crear un enlace temporal
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          
          // Obtener el nombre del archivo del header Content-Disposition
          const contentDisposition = response.headers.get('Content-Disposition')
          let filename = 'reporte'
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/)
            if (filenameMatch) {
              filename = filenameMatch[1]
            }
          }
          
          a.download = filename
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)

          this.showMessage('Reporte generado exitosamente', 'success')
        } else {
          const errorData = await response.json()
          this.showMessage('Error al generar el reporte: ' + (errorData.error || 'Error desconocido'), 'error')
        }
        
      } catch (error) {
        console.error('Error al generar reporte:', error)
        this.showMessage('Error al generar el reporte: ' + error.message, 'error')
      } finally {
        this.loading = false
      }
    },
  },
}
</script> 